import * as THREE from '@phdsoft/three';
// was: import { OCCLUDEE_DEPTH_MARGIN_NDC, OCCLUDEE_DEPTH_MARGIN_RELATIVE } from './software-depth-buffer';
import { CoverageSubSamples, OCCLUDEE_DEPTH_MARGIN_NDC, OCCLUDEE_DEPTH_MARGIN_RELATIVE } from './software-depth-buffer';

export interface BVHNode {
  bounds: THREE.Box3;
  left: BVHNode | null;
  right: BVHNode | null;
  objects: THREE.Object3D[];
  depth: number;
}

/**
 * How the fine tier tests a section's type meshes once its plates are all Occluded:
 * - 'triangles': every visible, in-frustum type mesh at triangle level (the original test).
 * - 'boxes': every visible, in-frustum type mesh by its own world box only (box faces tested
 *   per pixel, SoftwareDepthBuffer.isOccludedBox); a box that is not occluded keeps the section.
 *   Conservative (box ⊇ mesh). InstancedMesh / BatchedMesh / SkinnedMesh keep the triangle test
 *   (their geometry box does not bound the drawn instances / skinned vertices).
 * - 'boxes-then-triangles': the box as a fast reject, the triangles as the decision. Each
 *   type mesh's own world box is tested per pixel first; box occluded ⇒ mesh Occluded (no triangle
 *   test; counted in typeBoxesTested). Box not occluded ⇒ the mesh's triangles decide, exactly as in
 *   'triangles' (Visible / Occluded / NoCoverage; counted in typeTrianglesAfterBox), so a box that
 *   pokes out of the occluder (stray vertices, a box that is mostly air) no longer keeps a section
 *   whose triangles are hidden. InstancedMesh / BatchedMesh / SkinnedMesh go straight to the
 *   triangle test, meshes over maxTrianglesPerOccludeeTest to the own-OBB fallback, as in 'triangles'.
 * - 'none': type meshes are not tested (plate-only decision; the whole section is still hidden).
 *   May hide visible type meshes (openings, pillars, longitudinals running past the section
 *   boundary) — opt-in, for measurement only.
 */
// was: export type TypeMeshTestMode = 'triangles' | 'boxes' | 'none';
export type TypeMeshTestMode = 'triangles' | 'boxes' | 'boxes-then-triangles' | 'none';

/**
 * Why culling backed off:
 * - 'share': the window hid less than backoffMinCulledFraction of the candidate triangles;
 * - 'cost': the window's median cull took longer than the render time the hidden sections save
 *   (culled sections × backoffSectionCostMs, floor backoffMaxCullMs).
 *   was: more than backoffMaxCullMs while hiding less than backoffCostShareCeiling of the triangles.
 */
export type BackoffReason = 'share' | 'cost';

export interface CullingConfig {
  enabled: boolean;
  /** Depth buffer resolution. Applied at the next cull: the buffer is recreated when it changes (it was never applied after construction before). Any value other than 256 (after Number()) is treated as 128. */
  depthBufferResolution: 128 | 256;
  /**
   * Sub-samples per depth-buffer cell axis of the coverage mask (k x k sub-samples per cell); default 4.
   * 1 = no mask (legacy bounding-box rescue: a triangle that touches an empty pixel keeps the section
   * visible). 2 and 4 resolve slivers of a silhouette edge at sub-cell precision: the sliver that can still
   * keep a section visible is about 8 GPU px wide with 2 and about 4 px with 4 (at 1920 px wide); the cost
   * shows in CullingStats.coverageFullCells / coverageSubSampleCells. Applied at the next cull (the buffer
   * is recreated when it changes). A value that is not 1, 2 or 4 falls back to 4.
   * Residuals (k > 1), besides that sub-sample sliver class: coverage bits carry no depth, so an occludee
   * strip in front of a farther, earlier-tested occluder that covers no cell centre is hidden (up to ~1
   * cell ≈ 14 GPU px at 1920 wide); likewise behind occluder triangles partly beyond the far plane.
   * Measured by the GPU false-cull sweep; a per-cell farthest-coverage depth would remove it if needed.
   */
  coverageSubSamples: CoverageSubSamples;
  bvhLeafSize: number;
  minOccluderScreenSize: number;
  occlusionEnabled: boolean;
  debugVisualization: boolean;
  /** Max triangles for geometry-based occluder rasterization. Meshes exceeding this fall back to OBB. */
  maxTrianglesPerOccluderRasterization: number;
  /** Max triangles for geometry-based occludee testing. Meshes exceeding this fall back to OBB. */
  maxTrianglesPerOccludeeTest: number;
  /** Back off (show everything, stop culling) when the culled share of candidate triangles over the window is below this. */
  backoffMinCulledFraction: number;
  /** Number of real culls whose culled/candidate triangles decide the back-off. */
  backoffWindow: number;
  /** While backed off, one probe cull every this many cull calls. */
  backoffProbeInterval: number;
  /**
   * Cost rule, floor: a cull whose window median (ms, whole cull) is at most this never backs off
   * on cost, however few sections it hides. Above it, the cull must pay for itself: see
   * backoffSectionCostMs. A probe uses its own cull time.
   * was: back off when the median exceeds this and the culled share is below backoffCostShareCeiling.
   */
  backoffMaxCullMs: number;
  /**
   * Cost rule, economics: render time (ms of JS draw dispatch) that hiding one section saves,
   * measured at ≈ 0.2 ms (about 4 draw calls per section). The window backs off on 'cost' when its
   * median cull time exceeds max(backoffMaxCullMs, mean culled sections × this); a probe resumes when
   * its own cull time is within that budget. The culled-triangle share says nothing about this saving:
   * a view that hid 62 sections at a 21 % share cost 20 ms per frame and saved 0 ms (2026-10-27).
   */
  backoffSectionCostMs: number;
  // was: /** Cost rule: only back off on cost while the culled share of candidate triangles is below this. */
  // was: backoffCostShareCeiling: number;
  // was: /** Never back off (on share or cost) while the culled share of candidate triangles is at or above this. */
  // was: backoffNeverBelowShare: number;
  /**
   * THREE.Layers bit mask of the selection-outline layers (set by the outline owner,
   * Graphics). A section with any plate / type mesh on one of these layers is pinned:
   * its proxy stays visible (the X-ray outline renders through geometry, and three.js
   * renders nothing under an invisible proxy). 0 = no pinning.
   */
  pinnedLayerMask: number;
  // was: /** Fine-tier test of type meshes (see TypeMeshTestMode). Default 'boxes'. */
  /**
   * Fine-tier test of type meshes (see TypeMeshTestMode). Default 'boxes' (box only, may keep a
   * section by a box whose triangles are hidden); 'triangles' (triangles only), 'boxes-then-triangles'
   * (box as a fast reject, triangles as the decision) and 'none' (plate-only) stay selectable for A/B.
   */
  typeMeshTest: TypeMeshTestMode;
  /**
   * Occluder size gate: a surviving (or pinned) section's plates are rasterized only when the
   * screen rect of its section box, in depth-buffer pixels (clamped to the buffer), is at least
   * this. A box crossing the near or far plane counts as large. Smaller sections are shown
   * without being rasterized (under-occlusion only). 0 = no gate.
   */
  occluderMinRectPixels: number;
  /**
   * Occluder triangle budget per cull: in front-to-back order a section's plates are rasterized
   * only if their triangles fit in what is left of the budget; a section that would exceed it is
   * shown without being rasterized and later (smaller) sections may still fit. Infinity = no budget.
   */
  occluderTriangleBudget: number;
  /**
   * Occludee triangle test (testMeshOcclusion): first test triangles 0, k, 2k, … (k = this) and
   * return Visible on the first visible one; only then test the remaining triangles. Same decision
   * as k = 1, each triangle tested at most once. 1 = plain index order.
   */
  earlyAcceptStride: number;
  /**
   * Occludee depth margin, constant part, in NDC depth units ([0, 1] buffer depth). A covered pixel
   * centre of an occludee is Visible only when `stored >= 1 || depth < stored · marginScale − marginBias`,
   * marginScale / marginBias combining this and occludeeDepthMarginRelative for the camera
   * (SoftwareDepthBuffer.setOccludeeDepthMargin, pushed every cull). Strict: a tie (T-joint edge,
   * coplanar duplicate) is not visible. World size grows with the square of the view depth z:
   * Δz ≈ ndc · z² · (f − n) / (f · n) (app camera n 0.5, f 5000: ≈ 0.0075 mm at 5 m, 3 mm at 100 m).
   * Default OCCLUDEE_DEPTH_MARGIN_NDC (1.5e-7, ~2.5 float32 ulps below depth 1).
   * Legacy rule (ties visible, `depth < stored + 1e-6`): -1e-6 with occludeeDepthMarginRelative 0;
   * it differs from the old rule only at exact equality (old triangle test `depth <= stored + 1e-6`;
   * isOccludedOBB now `minDepth < stored + 1e-6`, old `minDepth <= stored` with no epsilon).
   * Guarded in setOccludeeDepthMargin: coerced with Number(); non-finite → the default; a negative
   * finite value is allowed (legacy A/B).
   */
  occludeeDepthMarginNdc: number;
  /**
   * Occludee depth margin, relative part: a fraction of the view depth z of the stored surface
   * (world Δz = relative · z: 2.5e-4 is 1.25 mm at 5 m, 2.5 cm at 100 m, 7.5 cm at 300 m), converted to
   * NDC for perspective and orthographic cameras, ignored for others. Absorbs mm-level joint noise.
   * With the NDC part the app camera's margin is about 1.3 mm at 5 m, 1.3 cm at 50 m, 2.8 cm at 100 m
   * and 10 cm at 300 m. Default OCCLUDEE_DEPTH_MARGIN_RELATIVE (2.5e-4); legacy rule: 0 (see
   * occludeeDepthMarginNdc for the exact-equality caveat). Guarded in setOccludeeDepthMargin:
   * coerced with Number(), non-finite → the default, clamped to [0, 0.5].
   */
  occludeeDepthMarginRelative: number;
}

export interface CullingStats {
  totalObjects: number;
  frustumCulled: number;
  occlusionCulled: number;
  visibleObjects: number;
  bvhTraversalTime: number;
  occlusionTestTime: number;
  frameTime: number;
  occludersRasterizedGeometry: number;
  /** Occluders that could not be rasterized from real geometry (over the triangle budget or no geometry) and were skipped — they do not occlude. */
  occludersSkipped: number;
  /**
   * Sections shown without rasterizing their plates because their section-box screen rect is
   * below config.occluderMinRectPixels (counted per section, only when it had plates to rasterize).
   */
  occludersSkippedSmall: number;
  /**
   * Sections shown without rasterizing their plates because their plate triangles would exceed
   * what is left of config.occluderTriangleBudget (counted per section).
   */
  occludersSkippedBudget: number;
  /**
   * Index, in this cull's sorted active order (front to back, the order sections are tested in), of the
   * first section refused for config.occluderTriangleBudget; -1 when no section was refused.
   */
  budgetExhaustedAt: number;
  occludeesTestedGeometry: number;
  // was: /** Section-level box tests (the coarse tier; heavy-mesh OBB fallbacks too). Type-mesh boxes count in typeBoxesTested. */
  /** Section-level box tests (the coarse tier) only. Heavy-mesh OBB fallbacks count in meshOBBTested, type-mesh boxes in typeBoxesTested. */
  occludeesTestedOBB: number;
  /** Fine-tier meshes over maxTrianglesPerOccludeeTest tested by their own OBB (isOccludedOBB) instead of their triangles. */
  meshOBBTested: number;
  // was: /** Type-mesh box tests of the fine tier (typeMeshTest 'boxes'). */
  /** Type-mesh box tests of the fine tier (typeMeshTest 'boxes' and 'boxes-then-triangles'). */
  typeBoxesTested: number;
  /**
   * typeMeshTest 'boxes-then-triangles': type meshes whose box was not occluded and that went on to
   * the triangle test (also counted in occludeesTestedGeometry). 0 in the other modes.
   */
  typeTrianglesAfterBox: number;
  /** Triangles (visible plates + type meshes) of the sections hidden by occlusion this frame. */
  culledTriangles: number;
  /** Triangles (visible plates + type meshes) of every section that went through the occlusion test this frame. */
  candidateTriangles: number;
  // was: /** Culling backed off because it hid too few triangles (every proxy shown; stats are those of the last real cull). */
  /** Culling backed off (every proxy shown; stats are those of the last real cull). See backoffReason. */
  backedOff: boolean;
  /** Why culling is backed off ('share' or 'cost'); null while culling (reset on resume / rebuild). */
  backoffReason: BackoffReason | null;

  // Phase instrumentation (reset per cull). Timers are milliseconds summed over the cull;
  // projectTimeMs is spent inside the plate / type tests and the rasterization, so it
  // overlaps plateTestTimeMs, typeTestTimeMs and rasterizeTimeMs.
  /** Sections hidden by the coarse tier (the section box). */
  coarseCulled: number;
  /** Sections hidden by the fine tier (plates at triangle level + type meshes per config.typeMeshTest). */
  fineCulled: number;
  /**
   * Sections whose tested plates were ALL Occluded but whose type-mesh test (triangles or
   * boxes) kept them visible — exactly the extra sections typeMeshTest 'none' would hide.
   * Always 0 in 'none'. In 'boxes-then-triangles' only the triangle test (or the heavy-mesh OBB
   * fallback) of a type mesh can keep a section: a box that is not occluded is not enough.
   */
  rescuedByTypes: number;
  /**
   * Sections kept because a plate's triangle test returned NoCoverage (no covered pixel centre,
   * no empty pixel touched). The first such plate stops the section's test; this includes plates
   * whose triangles were all masked or outside the depth range.
   */
  plateNoCoverage: number;
  /**
   * Sections kept because the deciding plate or type-mesh test was Visible at a covered pixel centre
   * clearly in front of the stored depth (beyond the occludee depth margin). Diagnostics.
   */
  keptByDepth: number;
  /**
   * Sections kept because the deciding plate or type-mesh test touched a pixel where nothing was
   * drawn (stored depth still the cleared value 1). Diagnostics.
   */
  keptByEmptyPixel: number;
  /**
   * Sections kept because the deciding plate or type-mesh test was not run per pixel (no geometry,
   * over the triangle limit, straddling the near plane, box projection failed, OBB fallback), or a
   * type box whose faces covered no pixel centre ('boxes' mode: isOccludedBox returns false with
   * lastVisibleReason None). Diagnostics.
   */
  keptUntested: number;
  /** Time in the coarse (section box) test. */
  coarseTimeMs: number;
  /** Time in the plate tests of the fine tier. */
  plateTestTimeMs: number;
  /** Time in the type-mesh tests of the fine tier (triangle or box tests per config.typeMeshTest; 0 in 'none'). */
  typeTestTimeMs: number;
  /** Time rasterizing the plates of surviving (and pinned) sections. */
  rasterizeTimeMs: number;
  /** Time projecting vertices in the depth buffer (cache hits included). */
  projectTimeMs: number;
  /** Vertices projected to screen space (cache misses only). */
  verticesProjected: number;
  /** Mesh projections reused from the depth buffer's one-mesh projection cache. */
  projectionCacheHits: number;
  /** Occludee triangles tested against the depth buffer (mesh triangles + box-face triangles of box tests). */
  trianglesTested: number;
  /** Occluder triangles rasterized into the depth buffer. */
  trianglesRasterized: number;
  /** Cells the coverage mask marked FULL by a triangle (always 0 with coverageSubSamples 1). */
  coverageFullCells: number;
  /** Partially covered cells whose k x k sub-samples were evaluated (always 0 with coverageSubSamples 1). */
  coverageSubSampleCells: number;
}

export function createDefaultCullingConfig(): CullingConfig {
  return {
    enabled: true,
    depthBufferResolution: 128,
    coverageSubSamples: 4,
    bvhLeafSize: 6,
    minOccluderScreenSize: 0.05,
    occlusionEnabled: true,
    debugVisualization: false,
    // Real section plates cap around ~12k triangles; 60k gives headroom so realistic
    // plates always rasterize their true silhouette instead of falling back to a
    // conservative bounding box that would over-occlude geometry behind them.
    maxTrianglesPerOccluderRasterization: 60000,
    maxTrianglesPerOccludeeTest: 20000,
    backoffMinCulledFraction: 0.05,
    backoffWindow: 30,
    backoffProbeInterval: 60,
    backoffMaxCullMs: 4,
    backoffSectionCostMs: 0.2,
    // was: backoffCostShareCeiling: 0.15,
    // was: backoffNeverBelowShare: 0.30,
    pinnedLayerMask: 0,
    // was: typeMeshTest: 'boxes',
    typeMeshTest: 'boxes',
    // 'boxes-then-triangles' (default in 0dac8055b) reverted 2026-10-26: it exposes the coverage mask's depth-free-bits residual on edge-on plate lines (false culls ×4, worst 175 px) until the mask carries a per-cell farthest depth; still selectable.
    occluderMinRectPixels: 64,
    occluderTriangleBudget: 40000,
    earlyAcceptStride: 8,
    occludeeDepthMarginNdc: OCCLUDEE_DEPTH_MARGIN_NDC,
    occludeeDepthMarginRelative: OCCLUDEE_DEPTH_MARGIN_RELATIVE
  };
}

export function createEmptyStats(): CullingStats {
  return {
    totalObjects: 0,
    frustumCulled: 0,
    occlusionCulled: 0,
    visibleObjects: 0,
    bvhTraversalTime: 0,
    occlusionTestTime: 0,
    frameTime: 0,
    occludersRasterizedGeometry: 0,
    occludersSkipped: 0,
    occludersSkippedSmall: 0,
    occludersSkippedBudget: 0,
    budgetExhaustedAt: -1,
    occludeesTestedGeometry: 0,
    occludeesTestedOBB: 0,
    meshOBBTested: 0,
    typeBoxesTested: 0,
    typeTrianglesAfterBox: 0,
    culledTriangles: 0,
    candidateTriangles: 0,
    backedOff: false,
    backoffReason: null,
    coarseCulled: 0,
    fineCulled: 0,
    rescuedByTypes: 0,
    plateNoCoverage: 0,
    keptByDepth: 0,
    keptByEmptyPixel: 0,
    keptUntested: 0,
    coarseTimeMs: 0,
    plateTestTimeMs: 0,
    typeTestTimeMs: 0,
    rasterizeTimeMs: 0,
    projectTimeMs: 0,
    verticesProjected: 0,
    projectionCacheHits: 0,
    trianglesTested: 0,
    trianglesRasterized: 0,
    coverageFullCells: 0,
    coverageSubSampleCells: 0
  };
}
